import React from 'react';
import { 
  Box, 
  HStack, 
  VStack,
  Text, 
  Button, 
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useLocation } from 'react-router-dom';

// The scheduling subdomain serves the same SPA but is meant to host only the
// booking page. So main-site nav links there must be real cross-origin links
// back to cloverridgetech.com, not in-app routes (which would stay on the
// subdomain and render broken). Everywhere else, normal client-side routing.
const ON_SCHEDULING_SUBDOMAIN =
  typeof window !== 'undefined' && window.location.hostname === 'scheduling.cloverridgetech.com';
const MAIN_SITE = 'https://cloverridgetech.com';

const Link = ({ to, children, ...rest }) =>
  ON_SCHEDULING_SUBDOMAIN ? (
    <a href={`${MAIN_SITE}${to}`} {...rest}>{children}</a>
  ) : (
    <RouterLink to={to} {...rest}>{children}</RouterLink>
  );

const Header = () => {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Show mobile menu on smaller screens
  const showMobileMenu = useBreakpointValue({ base: true, lg: false });

  const NavLinks = ({ isMobile = false, onLinkClick = () => {} }) => {
    const linkProps = {
      color: "white",
      fontSize: "sm",
      fontWeight: "medium",
      _hover: { color: "green.500" },
      transition: "color 0.2s",
      px: 2,
      py: isMobile ? 3 : 0,
      w: isMobile ? "full" : "auto",
      textAlign: isMobile ? "left" : "center"
    };

    if (isMobile) {
      return (
        <VStack spacing={2} align="start" w="full">
          <Link to="/" onClick={onLinkClick}>
            <Text
              {...linkProps}
              color={location.pathname === '/' ? "green.500" : "white"}
            >
              Home
            </Text>
          </Link>
          <Link to="/about" onClick={onLinkClick}>
            <Text
              {...linkProps}
              color={location.pathname === '/about' ? "green.500" : "white"}
            >
              About
            </Text>
          </Link>
          <Link to="/engineers" onClick={onLinkClick}>
            <Text
              {...linkProps}
              color={location.pathname === '/engineers' ? "green.500" : "white"}
            >
              Engineers
            </Text>
          </Link>
          <Link to="/projects" onClick={onLinkClick}>
            <Text
              {...linkProps}
              color={location.pathname === '/projects' ? "green.500" : "white"}
            >
              Projects
            </Text>
          </Link>
          <Link to="/articles" onClick={onLinkClick}>
            <Text
              {...linkProps}
              color={location.pathname === '/articles' ? "green.500" : "white"}
            >
              Articles
            </Text>
          </Link>
          <Box
            as="a"
            href="https://learning.cloverridgetech.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onLinkClick}
          >
            <Text
              {...linkProps}
              color="white"
            >
              Learning
            </Text>
          </Box>

          <Link to="/contact" onClick={onLinkClick}>
            <Button
              bg={location.pathname === '/contact' ? "green.600" : "green.500"}
              color="white"
              size="md"
              px={8}
              fontSize="sm"
              fontWeight="medium"
              _hover={{ bg: "green.600" }}
              w="full"
              mt={4}
            >
              Contact
            </Button>
          </Link>
        </VStack>
      );
    }

    // Desktop horizontal layout
    return (
      <HStack spacing={{ base: 4, md: 6, lg: 12 }} align="center">
        <Link to="/">
          <Text
            {...linkProps}
            color={location.pathname === '/' ? "green.500" : "white"}
          >
            Home
          </Text>
        </Link>
        <Link to="/about">
          <Text
            {...linkProps}
            color={location.pathname === '/about' ? "green.500" : "white"}
          >
            About
          </Text>
        </Link>
        <Link to="/engineers">
          <Text
            {...linkProps}
            color={location.pathname === '/engineers' ? "green.500" : "white"}
          >
            Engineers
          </Text>
        </Link>
        <Link to="/projects">
          <Text
            {...linkProps}
            color={location.pathname === '/projects' ? "green.500" : "white"}
          >
            Projects
          </Text>
        </Link>
        <Link to="/articles">
          <Text
            {...linkProps}
            color={location.pathname === '/articles' ? "green.500" : "white"}
          >
            Articles
          </Text>
        </Link>
        <Box
          as="a"
          href="https://learning.cloverridgetech.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text
            {...linkProps}
            color="white"
          >
            Learning
          </Text>
        </Box>
      </HStack>
    );
  };

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={10}
      bg="black"
      borderBottom="1px solid"
      borderColor="gray.800"
      mb={0}
    >
      <Box px={{ base: 4, md: 8 }} py={4}>
        <HStack justify="space-between" align="center" w="100%" maxW="6xl" mx="auto">
          {/* Logo */}
          <Link to="/">
            <HStack spacing={2} cursor="pointer">
              <Text 
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }} 
                fontWeight="bold" 
                color="white" 
                letterSpacing="-0.02em"
              >
                clover ridge
              </Text>
              <Text 
                fontSize={{ base: "xs", md: "sm" }} 
                color="gray.400" 
                fontWeight="normal"
                display={{ base: "none", sm: "block" }}
              >
                technologies
              </Text>
            </HStack>
          </Link>

          {/* Desktop Navigation */}
          {!showMobileMenu && (
            <>
              <HStack spacing={{ base: 6, md: 8, lg: 12 }} flex={1} justify="center">
                <NavLinks />
              </HStack>
              
              {/* Desktop Contact Button */}
              <Link to="/contact">
                <Button
                  bg={location.pathname === '/contact' ? "green.600" : "green.500"}
                  color="white"
                  size="sm"
                  px={6}
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ bg: "green.600" }}
                  flexShrink={0}
                >
                  Contact
                </Button>
              </Link>
            </>
          )}

          {/* Mobile Menu Button */}
          {showMobileMenu && (
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={onOpen}
              size="lg"
            />
          )}
        </HStack>
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="black" color="white">
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor="gray.800">
            <Text fontSize="lg" fontWeight="bold">
              Menu
            </Text>
          </DrawerHeader>
          <DrawerBody pt={8}>
            <NavLinks isMobile={true} onLinkClick={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;